import type { ActivityMarker, Breakthrough, RaceEvent } from './chartData';
import type { ProgressionFilterFields } from './ProgressionFilterFields';

/** XSS on one local day (`YYYY-MM-DD`) from the activities that matched the filters. */
export type MatchedDay = { date: string; xss: number; xlss: number; xhss: number; xpss: number };

/**
 * What /my-fitness/activity_statistics returns (ShowsUserTrait::_getActivityStats). Sums, averages
 * and maxima are over the activities the filters match; the start_/end_/ramp_rate_ figures describe
 * the athlete's fitness over the date range and are never narrowed by the activity filters.
 * Times are seconds, distances are in the athlete's own units, HIE is in joules, sfd is seconds.
 */
/** The activity behind a "Max" figure: its name, its local date and where to open it. */
export type MaxActivity = { name: string; date: string; url: string };

export type PeriodStats = {
  count: number;
  avg_distance: number; total_distance: number; max_distance: number;
  avg_time: number; total_time: number; max_time: number;
  avg_xss: number; total_xss: number; max_xss: number;
  total_xpss: number; total_xhss: number; total_xlss: number;
  total_tgc: number; avg_tgc: number; max_tgc: number;
  total_tgf: number; avg_tgf: number; max_tgf: number;
  total_work: number; total_calories: number; avg_calories: number; max_calories: number;
  avg_sfd: number; max_sfd: number;
  /** Where each Max figure came from, keyed time / distance / xss / calories / tgf / tgc. */
  max_activities?: Record<string, MaxActivity>;
  /** Real elapsed days in the range; divide by 7 for every "per week" figure. */
  range_days: number;
  start_ts: number; end_ts: number;
  /** True when a filter beyond the date range narrowed the activities. */
  filtered: boolean;
  ramp_rate: number; ramp_rate_peak: number; ramp_rate_high: number; ramp_rate_low: number;
  /** The Improvement Rate whose target weekly ramp is closest to what actually happened (e.g. "Ease Off Period", "Moderate-1"). */
  ramp_rate_label: string;
  start_tl: number; end_tl: number; start_ptl: number; end_ptl: number;
  start_htl: number; end_htl: number; start_ltl: number; end_ltl: number;
  start_ftp: number; end_ftp: number; start_ltp: number; end_ltp: number;
  start_pp: number; end_pp: number; start_hie: number; end_hie: number;
  /** Present only while a filter is narrowing the activities: which days' XSS came from the ones that matched. */
  matched_days?: MatchedDay[] | null;
  [key: string]: number | boolean | string | MatchedDay[] | Record<string, MaxActivity> | null | undefined;
};

export const DEFAULT_STATS_URL = '/my-fitness/activity_statistics';

/** Period stats for the current filters. Pass an AbortSignal to drop a request that's been superseded. */
export async function fetchPeriodStats(
  fields: ProgressionFilterFields,
  url: string = DEFAULT_STATS_URL,
  signal?: AbortSignal
): Promise<PeriodStats> {
  const response = await fetch(`${url}${url.includes('?') ? '&' : '?'}${fields.toQuery()}`, {
    signal,
    headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
  });
  if (!response.ok) throw new Error(`Period stats request failed (${response.status})`);
  return response.json();
}

export const DEFAULT_MEASURES_URL = '/my-fitness/measures';

/** One day of the athlete's fitness history, as /my-fitness/measures returns it. Only what's read so far. */
export type MeasureRow = {
  start_date: string;
  generated_at: string;
  /** Focus duration at that day's signature and training load, in minutes. */
  focus_strain?: number;
  /** Body weight (kg) as of this row's activity. */
  weight?: number;
  [key: string]: unknown;
};

export type MeasuresResponse = {
  history: MeasureRow[] | null;
  /** Breakthrough activities, with the medal colour they earned when they had one. */
  ba?: Breakthrough[] | null;
  /** Activities that are flagged, or whose signature or training load was set by hand. */
  markers?: ActivityMarker[] | null;
  /** Activities tagged Race or Event. */
  race_events?: RaceEvent[] | null;
  [key: string]: unknown;
};

/** The athlete's fitness history from `periodStart` (unix seconds) on, including projected days after today. */
export async function fetchMeasures(
  periodStart: number,
  url: string = DEFAULT_MEASURES_URL,
  signal?: AbortSignal
): Promise<MeasuresResponse> {
  const query = new URLSearchParams({ period: String(periodStart), show_all: 'true' });
  const response = await fetch(`${url}${url.includes('?') ? '&' : '?'}${query}`, {
    signal,
    headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
  });
  if (!response.ok) throw new Error(`Measures request failed (${response.status})`);
  return response.json();
}
